import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "upcoming-draws upcoming-draws-view" }
const _hoisted_2 = { class: "lucky-numbers-section" }

import { ref, onMounted, watch } from "vue";
import LotterySection from '@/components/LotterySection.vue'
import FavoritesSection from "@/components/FavoritesSection.vue";
import MostPlayedSection from "@/components/MostPlayedSection.vue";
import LuckyNumbersSection from "@/components/LuckyNumbersSection.vue";
import { useUpcomingDrawsStore } from "@/stores/upcomingDraws";
import type { UpcomingDraw } from "@/types/lottery";
import { useRouter } from 'vue-router';
import { useAuthStore } from "@/stores/auth";


export default /*@__PURE__*/_defineComponent({
  __name: 'UpcomingDrawsView',
  setup(__props) {

const authStore = useAuthStore()
const router = useRouter();
const favorites = ref<UpcomingDraw[]>([]);
const mostPlayed = ref<UpcomingDraw[]>([]);
const upcomingDrawsStore = useUpcomingDrawsStore();

// Load favorites from local storage
const loadFavorites = () => {
  const savedFavorites = localStorage.getItem('favoriteDraws');
  if (savedFavorites) {
    const cachedFavorites = JSON.parse(savedFavorites);
    favorites.value = syncWithFreshData(cachedFavorites);
  }
};

const syncWithFreshData = (cachedData: UpcomingDraw[]) => {
  return cachedData.map(cachedItem => {
    const freshData = upcomingDrawsStore.draws.find(draw => draw.game_slug === cachedItem.game_slug);
    if (freshData) {
      return {
        ...cachedItem,
        draw_date: freshData.draw_date,
        cutoff: freshData.cutoff,
        game_name: freshData.game_name,
        game_logo: freshData.game_logo,
      };
    }
    return cachedItem;
  });
};

// Save favorites to local storage
const saveFavorites = () => {
  localStorage.setItem('favoriteDraws', JSON.stringify(favorites.value));
};

// Load most played from local storage and sync with fresh data
const loadMostPlayed = () => {
  const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
  if (savedMostPlayed) {
    const mostPlayedData = JSON.parse(savedMostPlayed);
    // Sync with fresh data and sort by play count, take top 5
    const syncedData = syncWithFreshData(mostPlayedData);
    mostPlayed.value = syncedData
      .sort((a: UpcomingDraw & { playCount: number }, b: UpcomingDraw & { playCount: number }) => b.playCount - a.playCount)
      .slice(0, 5);
  }
};

// Save most played to local storage
const saveMostPlayed = (draw: UpcomingDraw) => {
  const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
  let mostPlayedData = savedMostPlayed ? JSON.parse(savedMostPlayed) : [];
  
  // Find if draw already exists in most played
  const existingIndex = mostPlayedData.findIndex((item: UpcomingDraw & { playCount: number }) => item.id === draw.id);
  
  if (existingIndex !== -1) {
    // Increment play count if draw exists
    mostPlayedData[existingIndex].playCount++;
  } else {
    // Add new draw with play count 1
    mostPlayedData.push({ ...draw, playCount: 1 });
  }
  
  // Sort by play count and take top 5
  mostPlayedData = mostPlayedData
    .sort((a: UpcomingDraw & { playCount: number }, b: UpcomingDraw & { playCount: number }) => b.playCount - a.playCount)
    .slice(0, 5);
  
  // Save to local storage
  localStorage.setItem('mostPlayedDraws', JSON.stringify(mostPlayedData));
  
  // Update mostPlayed ref
  mostPlayed.value = mostPlayedData;
};

const handleBet = (draw: UpcomingDraw) => {
  saveMostPlayed(draw); // Track this bet
  router.push(`/lottery/${draw.game_slug}`);
};

const handleFavorite = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return;
  const index = favorites.value.findIndex(fav => fav.id === draw.id);
  if (index === -1) {
    favorites.value.push(draw);
  } else {
    favorites.value.splice(index, 1);
  }
  saveFavorites();
};

const isFavorited = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return false;
  return favorites.value.some(fav => fav.id === draw.id);
};

const removeLottery = (lotteryToRemove: UpcomingDraw) => {
  const idx = upcomingDrawsStore.draws.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    upcomingDrawsStore.draws.splice(idx, 1);
    if (upcomingDrawsStore.draws.length === 0) {
      upcomingDrawsStore.fetchDraws(1);
    }
  }
};

watch(
  () => upcomingDrawsStore.draws,
  () => {
    if (upcomingDrawsStore.draws.length > 0) {
      loadFavorites();
      loadMostPlayed();
    }
  },
  { deep: true }
);

onMounted(async () => {
  // Initialize auth store from sessionStorage
  authStore.initializeFromSession();
  
  loadFavorites();
  loadMostPlayed();
  await upcomingDrawsStore.fetchDraws(1);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LotterySection, {
      title: _ctx.$t('sections.upcomingDraws'),
      lotteries: _unref(upcomingDrawsStore).draws,
      isFavorited: isFavorited,
      showFilters: true,
      onBet: handleBet,
      onFavorite: handleFavorite,
      onRemoveLottery: removeLottery
    }, null, 8, ["title", "lotteries"]),
    _createVNode(FavoritesSection, {
      favorites: favorites.value,
      isFavorited: isFavorited,
      onFavorite: handleFavorite
    }, null, 8, ["favorites"]),
    _createVNode(MostPlayedSection, {
      mostPlayed: mostPlayed.value,
      isFavorited: isFavorited,
      onFavorite: handleFavorite
    }, null, 8, ["mostPlayed"]),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(LuckyNumbersSection, {
        title: _ctx.$t('sections.luckyNumbers'),
        games: _unref(upcomingDrawsStore).draws,
        isFavorited: isFavorited,
        titleAlign: "left",
        dateFilterOnly: false,
        onBet: handleBet,
        onFavorite: handleFavorite,
        onRemove: removeLottery
      }, null, 8, ["title", "games"])
    ])
  ]))
}
}

})